import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    ShieldAlert, FileWarning, IdCard, ArrowLeft, Plus, Upload, Trash2, FileText,
    Download, Loader2, CheckCircle2, AlertTriangle, Search, X, Lock, Archive, FolderOpen,
} from "lucide-react";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm";
const labelCls = "text-[11px] uppercase tracking-wider text-ink-500 mb-1 block";

const RISK_LEVEL_STYLE = {
    basso: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    medio: "text-amber-400 border-amber-500/40 bg-amber-500/10",
    alto: "text-red-400 border-red-500/40 bg-red-500/10",
};

const RISK_QUESTIONS = [
    { key: "is_pep", label: "Il cliente è una Persona Politicamente Esposta (PEP)?" },
    { key: "pep_family_or_associate", label: "È familiare o stretto collaboratore di un PEP?" },
    { key: "high_risk_country", label: "Residenza/cittadinanza in un paese ad alto rischio?" },
    { key: "cash_operation", label: "L'operazione prevede pagamento in contanti?" },
    { key: "cash_amount_over_threshold", label: "L'importo in contanti supera la soglia di legge (2.000€)?" },
    { key: "complex_ownership_structure", label: "Struttura proprietaria complessa/opaca (persona giuridica)?" },
    { key: "beneficial_owner_unverified", label: "Il titolare effettivo non è stato identificato/verificato?" },
    { key: "non_cooperative_client", label: "Il cliente è reticente o non collaborativo?" },
    { key: "unusual_operation", label: "L'operazione è anomala rispetto al profilo economico del cliente?" },
    { key: "new_client_no_history", label: "È un nuovo cliente senza storico con l'agenzia?" },
];

const DOC_KIND_OPTIONS = [
    { value: "carta_identita", label: "Carta d'Identità" },
    { value: "passaporto", label: "Passaporto" },
    { value: "patente", label: "Patente di Guida" },
    { value: "altro", label: "Altro documento" },
];

export default function CrmAml() {
    const [status, setStatus] = useState(null);
    const [view, setView] = useState("list");
    const [activeCase, setActiveCase] = useState(null);
    const [tab, setTab] = useState("nuova");

    useEffect(() => {
        api.get("/crm/aml/disclaimer/status").then((r) => setStatus(r.data)).catch(() => setStatus({ accepted: false }));
    }, []);

    if (!status) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>;

    return (
        <div data-testid="crm-aml-page">
            <div className="mb-8">
                <span className="label-eyebrow text-gold">VD Manager · Compliance</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <ShieldAlert size={22} className="text-gold" /> Antiriciclaggio (AML)
                </h2>
                <p className="text-ink-400 text-sm mt-1">Strumento operativo per la raccolta dati di adeguata verifica della clientela.</p>
            </div>

            {status.accepted && (
                <div className="flex items-center gap-2 mb-7 border-b border-white/10" data-testid="aml-tabs">
                    <button
                        onClick={() => { setTab("nuova"); setView("list"); setActiveCase(null); }}
                        data-testid="aml-tab-nuova"
                        className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors -mb-px ${tab === "nuova" ? "border-gold text-gold" : "border-transparent text-ink-400 hover:text-ink-100"}`}
                    >
                        <FolderOpen size={14} /> Nuova Pratica
                    </button>
                    <button
                        onClick={() => setTab("archivio")}
                        data-testid="aml-tab-archivio"
                        className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors -mb-px ${tab === "archivio" ? "border-gold text-gold" : "border-transparent text-ink-400 hover:text-ink-100"}`}
                    >
                        <Archive size={14} /> Archivio Pratiche
                    </button>
                </div>
            )}

            {!status.accepted ? (
                <DisclaimerGate text={status.text} onAccepted={() => setStatus({ ...status, accepted: true })} />
            ) : tab === "archivio" ? (
                <ArchivioPratiche />
            ) : view === "detail" && activeCase ? (
                <CaseDetail caseId={activeCase} onBack={() => { setView("list"); setActiveCase(null); }} />
            ) : (
                <CasesList onOpen={(id) => { setActiveCase(id); setView("detail"); }} />
            )}
        </div>
    );
}

function ArchivioPratiche() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [riskFilter, setRiskFilter] = useState("");
    const [downloadingId, setDownloadingId] = useState(null);
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = useCallback(async (targetPage = 1, append = false) => {
        try {
            const params = { page: targetPage, page_size: 50 };
            if (q) params.q = q;
            if (riskFilter) params.risk_level = riskFilter;
            const r = await api.get("/crm/aml/archivio", { params });
            setData((prev) => append && prev ? { ...r.data, items: [...prev.items, ...r.data.items] } : r.data);
            setPage(targetPage);
        } catch (e) { if (!append) setData({ items: [], total: 0, has_more: false }); }
        finally { setLoadingMore(false); }
    }, [q, riskFilter]);

    useEffect(() => { load(1, false); }, [load]);

    const loadMore = () => { setLoadingMore(true); load(page + 1, true); };

    const downloadVerbale = async (id) => {
        setDownloadingId(id);
        try {
            const res = await api.get(`/crm/aml/archivio/${id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `verbale-aml-${id}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (e) { alert("Errore durante il download del verbale archiviato"); }
        finally { setDownloadingId(null); }
    };

    return (
        <div data-testid="aml-archivio">
            <div className="flex items-start gap-3 border border-gold/30 bg-gold/5 p-4 mb-6" data-testid="aml-archivio-retention-notice">
                <Lock size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <div className="text-xs text-ink-200 leading-relaxed">
                    <span className="text-gold font-semibold uppercase tracking-wider">Archivio blindato · Zero Access</span>
                    <p className="mt-1">
                        Ogni verbale è cifrato a riposo con <b>AES-256-GCM</b> con chiave dedicata alla tua agenzia:
                        nessun operatore VendiDirect può accedervi o decifrarlo. Ai sensi del D.Lgs. 231/2007 la
                        conservazione minima di legge di questi documenti è di <b>10 anni</b> dalla data dell'operazione;
                        la responsabilità della conservazione resta in capo alla tua agenzia.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="text-xs text-ink-400">
                    Verbali archiviati: <b className="text-ink-50">{data?.total ?? 0}</b>
                </div>
                <div className="flex items-center gap-2">
                    <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} className={`${inputCls} w-40`} data-testid="aml-archivio-risk-filter">
                        <option value="">Tutti i rischi</option>
                        <option value="basso">Rischio Basso</option>
                        <option value="medio">Rischio Medio</option>
                        <option value="alto">Rischio Alto</option>
                    </select>
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-2.5 text-ink-500" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className={`${inputCls} pl-8 w-48`} data-testid="aml-archivio-search-input" />
                    </div>
                </div>
            </div>

            {!data ? (
                <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>
            ) : data.items.length === 0 ? (
                <div className="border border-white/10 border-dashed p-10 text-center text-sm text-ink-400" data-testid="aml-archivio-empty">
                    Nessun verbale archiviato finora. I verbali generati dalla tab "Nuova Pratica" compariranno qui.
                </div>
            ) : (
                <div className="space-y-2" data-testid="aml-archivio-rows">
                    {data.items.map((v) => (
                        <div key={v.id} className="border border-white/10 hover:border-gold/40 p-4 flex items-center justify-between gap-4 transition-colors" data-testid={`aml-archivio-row-${v.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <Lock size={14} className="text-gold flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm text-ink-50 font-medium truncate flex items-center gap-2">
                                        {v.client_name || "Cliente senza nome"}
                                        {v.case_deleted && (
                                            <span className="text-[9px] uppercase px-1.5 py-0.5 border border-white/20 text-ink-500" data-testid={`aml-archivio-orphan-${v.id}`}>Pratica eliminata</span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-ink-500 mt-0.5">
                                        Generato il {new Date(v.generated_at).toLocaleDateString("it-IT")} alle {new Date(v.generated_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · da {v.generated_by || "—"}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {v.risk_level ? (
                                    <span className={`text-[10px] uppercase px-2 py-1 border ${RISK_LEVEL_STYLE[v.risk_level]}`}>{v.risk_level}</span>
                                ) : (
                                    <span className="text-[10px] uppercase px-2 py-1 border border-white/15 text-ink-500">n.d.</span>
                                )}
                                <button
                                    onClick={() => downloadVerbale(v.id)}
                                    disabled={downloadingId === v.id}
                                    className="cta-outline px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
                                    data-testid={`aml-archivio-download-${v.id}`}
                                >
                                    {downloadingId === v.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
                                </button>
                            </div>
                        </div>
                    ))}
                    {data.has_more && (
                        <div className="text-center pt-3">
                            <button onClick={loadMore} disabled={loadingMore} className="cta-outline px-5 py-2 text-xs disabled:opacity-50" data-testid="aml-archivio-load-more">
                                {loadingMore ? "Caricamento…" : "Carica altri verbali"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function DisclaimerGate({ text, onAccepted }) {
    const [checked, setChecked] = useState(false);
    const [saving, setSaving] = useState(false);

    const accept = async () => {
        setSaving(true);
        try { await api.post("/crm/aml/disclaimer/accept"); onAccepted(); }
        catch (e) { alert("Errore durante l'accettazione"); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-amber-500/40 bg-amber-900/5 p-8 max-w-2xl" data-testid="aml-disclaimer-gate">
            <div className="flex items-center gap-2 text-amber-300 mb-4">
                <FileWarning size={18} /> <span className="text-xs uppercase tracking-wider font-semibold">Disclaimer legale obbligatorio</span>
            </div>
            <p className="text-sm text-ink-100 leading-relaxed mb-6">{text}</p>
            <label className="flex items-start gap-3 mb-6 cursor-pointer">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1 accent-gold" data-testid="aml-disclaimer-checkbox" />
                <span className="text-sm text-ink-300">Ho letto e compreso quanto sopra, e accetto le condizioni d'uso dello strumento AML per conto della mia agenzia.</span>
            </label>
            <button
                data-testid="aml-disclaimer-accept-btn"
                onClick={accept}
                disabled={!checked || saving}
                className="cta-gold px-8 py-3 text-sm font-medium disabled:opacity-40"
            >
                {saving ? "Attendere…" : "Accetto e Continuo"}
            </button>
        </div>
    );
}

function CasesList({ onOpen }) {
    const [data, setData] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        try { const r = await api.get("/crm/aml/cases", { params: q ? { q } : {} }); setData(r.data); }
        catch (e) { setData({ items: [], counts: {} }); }
    }, [q]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="aml-cases-list">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-4 text-xs text-ink-400">
                    <span>Totale: <b className="text-ink-50">{data?.counts?.totale || 0}</b></span>
                    <span>Bozze: <b className="text-ink-50">{data?.counts?.bozza || 0}</b></span>
                    <span>Completate: <b className="text-ink-50">{data?.counts?.completata || 0}</b></span>
                    <span className="text-red-400">Rischio Alto: <b>{data?.counts?.alto || 0}</b></span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-2.5 text-ink-500" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente…" className={`${inputCls} pl-8 w-48`} data-testid="aml-search-input" />
                    </div>
                    <button data-testid="aml-new-case-btn" onClick={() => setShowNew(true)} className="cta-gold px-4 py-2 text-xs flex items-center gap-1.5">
                        <Plus size={13} /> Nuova Pratica
                    </button>
                </div>
            </div>

            {!data ? (
                <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>
            ) : data.items.length === 0 ? (
                <div className="border border-white/10 border-dashed p-10 text-center text-sm text-ink-400" data-testid="aml-empty">
                    Nessuna pratica AML creata finora.
                </div>
            ) : (
                <div className="space-y-2" data-testid="aml-cases-rows">
                    {data.items.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`aml-case-row-${c.id}`}
                            onClick={() => onOpen(c.id)}
                            className="w-full text-left border border-white/10 hover:border-gold/40 p-4 flex items-center justify-between gap-4 transition-colors"
                        >
                            <div>
                                <div className="text-sm text-ink-50 font-medium">{c.client?.full_name || "Cliente senza nome"}</div>
                                <div className="text-[11px] text-ink-500 mt-0.5">{c.operation?.tipo_prestazione || "—"} · creata il {new Date(c.created_at).toLocaleDateString("it-IT")}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                {c.risk_level && (
                                    <span className={`text-[10px] uppercase px-2 py-1 border ${RISK_LEVEL_STYLE[c.risk_level]}`}>{c.risk_level}</span>
                                )}
                                <span className={`text-[10px] uppercase px-2 py-1 border ${c.status === "completata" ? "border-gold/40 text-gold" : "border-white/15 text-ink-400"}`}>{c.status}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {showNew && <NewCaseModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); onOpen(id); }} />}
        </div>
    );
}

function NewCaseModal({ onClose, onCreated }) {
    const [source, setSource] = useState("manual");
    const [leads, setLeads] = useState([]);
    const [leadId, setLeadId] = useState("");
    const [fullName, setFullName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (source === "lead") api.get("/crm/leads").then((r) => setLeads(r.data.items || r.data || [])).catch(() => setLeads([]));
    }, [source]);

    const create = async () => {
        setError("");
        if (source === "manual" && !fullName.trim()) { setError("Inserisci il nome del cliente"); return; }
        if (source === "lead" && !leadId) { setError("Seleziona un lead"); return; }
        setSaving(true);
        try {
            const r = await api.post("/crm/aml/cases", {
                client_source: source, lead_id: source === "lead" ? leadId : null,
                client: source === "manual" ? { full_name: fullName } : {}, operation: {},
            });
            onCreated(r.data.id);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore creazione pratica");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="aml-new-case-modal">
            <div className="bg-ink-950 border border-gold/40 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-5">
                    <h3 className="font-serif text-xl text-ink-50">Nuova Pratica AML</h3>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                </div>
                <div className="flex gap-2 mb-4">
                    <button onClick={() => setSource("manual")} className={`flex-1 py-2 text-xs border ${source === "manual" ? "border-gold text-gold" : "border-white/15 text-ink-400"}`} data-testid="aml-source-manual">Dati Manuali</button>
                    <button onClick={() => setSource("lead")} className={`flex-1 py-2 text-xs border ${source === "lead" ? "border-gold text-gold" : "border-white/15 text-ink-400"}`} data-testid="aml-source-lead">Da Lead CRM</button>
                </div>
                {source === "manual" ? (
                    <div>
                        <label className={labelCls}>Nome e Cognome / Ragione Sociale</label>
                        <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} data-testid="aml-new-fullname" />
                    </div>
                ) : (
                    <div>
                        <label className={labelCls}>Seleziona Lead</label>
                        <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className={inputCls} data-testid="aml-new-lead-select">
                            <option value="">— Seleziona —</option>
                            {leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    </div>
                )}
                {error && <div className="text-red-300 text-xs mt-3" data-testid="aml-new-case-error">{error}</div>}
                <button onClick={create} disabled={saving} className="cta-gold w-full mt-6 py-3 text-sm disabled:opacity-50" data-testid="aml-new-case-submit">
                    {saving ? "Creazione…" : "Crea Pratica"}
                </button>
            </div>
        </div>
    );
}

function CaseDetail({ caseId, onBack }) {
    const [c, setC] = useState(null);
    const [client, setClient] = useState({});
    const [operation, setOperation] = useState({});
    const [verification, setVerification] = useState({});
    const [riskAnswers, setRiskAnswers] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");
    const [generating, setGenerating] = useState(false);
    const fileInputRef = useRef(null);
    const [docKind, setDocKind] = useState("carta_identita");
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        const r = await api.get(`/crm/aml/cases/${caseId}`);
        setC(r.data);
        setClient(r.data.client || {});
        setOperation(r.data.operation || {});
        setVerification(r.data.verification || {});
        setRiskAnswers(r.data.risk_answers || {});
    }, [caseId]);

    useEffect(() => { load(); }, [load]);

    const liveRisk = React.useMemo(() => {
        const weights = { is_pep: 35, pep_family_or_associate: 25, high_risk_country: 30, cash_operation: 15, cash_amount_over_threshold: 15, complex_ownership_structure: 20, beneficial_owner_unverified: 25, non_cooperative_client: 30, unusual_operation: 20, new_client_no_history: 5 };
        let score = 0;
        Object.entries(weights).forEach(([k, w]) => { if (riskAnswers[k]) score += w; });
        score = Math.min(100, score);
        const level = score >= 50 ? "alto" : score >= 20 ? "medio" : "basso";
        return { score, level };
    }, [riskAnswers]);

    const save = async () => {
        setSaving(true); setSaveMsg("");
        try {
            const r = await api.put(`/crm/aml/cases/${caseId}`, { client, operation, verification, risk_answers: riskAnswers });
            setC(r.data);
            setSaveMsg("Modifiche salvate");
        } catch (e) { setSaveMsg("Errore nel salvataggio"); }
        finally { setSaving(false); setTimeout(() => setSaveMsg(""), 3000); }
    };

    const uploadFile = async (file) => {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("doc_kind", docKind);
        try {
            await api.post(`/crm/aml/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            await load();
        } catch (e) { alert(e?.response?.data?.detail || "Errore caricamento documento"); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
    };

    const downloadDoc = async (docId, filename) => {
        try {
            const res = await api.get(`/crm/aml/cases/${caseId}/documents/${docId}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || "documento-aml";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (e) { alert("Errore durante il download del documento"); }
    };

    const deleteDoc = async (docId) => {
        if (!window.confirm("Eliminare questo documento?")) return;
        await api.delete(`/crm/aml/cases/${caseId}/documents/${docId}`);
        await load();
    };

    const generateVerbale = async () => {
        await save();
        setGenerating(true);
        try {
            const res = await api.get(`/crm/aml/cases/${caseId}/verbale`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `verbale-aml-${caseId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
            await load();
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore generazione verbale");
        } finally { setGenerating(false); }
    };

    if (!c) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento pratica…</div>;

    return (
        <div data-testid="aml-case-detail" className="space-y-6">
            <div className="flex items-center justify-between">
                <button onClick={onBack} data-testid="aml-back-to-list" className="text-ink-400 hover:text-gold text-sm flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Torna all'elenco
                </button>
                <div className="flex items-center gap-2">
                    {saveMsg && <span className="text-xs text-ink-400">{saveMsg}</span>}
                    <button onClick={save} disabled={saving} className="cta-outline px-4 py-2 text-xs disabled:opacity-50" data-testid="aml-save-btn">
                        {saving ? "Salvataggio…" : "Salva modifiche"}
                    </button>
                    <button onClick={generateVerbale} disabled={generating} data-testid="aml-generate-verbale-btn" className="cta-gold px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">
                        <FileText size={13} /> {generating ? "Generazione…" : "Genera Verbale AML"}
                    </button>
                </div>
            </div>

            <Section title="1. Dati del Cliente" icon={IdCard}>
                <label className="flex items-center gap-2 mb-4 text-xs text-ink-400">
                    <input type="checkbox" checked={!!client.is_legal_entity} onChange={(e) => setClient({ ...client, is_legal_entity: e.target.checked })} className="accent-gold" data-testid="aml-client-legal-entity" />
                    Cliente persona giuridica (società)
                </label>
                <Grid>
                    <Field label={client.is_legal_entity ? "Ragione Sociale" : "Nome e Cognome"} value={client.full_name} onChange={(v) => setClient({ ...client, full_name: v })} testid="aml-client-fullname" />
                    <Field label="Codice Fiscale" value={client.fiscal_code} onChange={(v) => setClient({ ...client, fiscal_code: v })} />
                    {client.is_legal_entity ? (
                        <Field label="Partita IVA" value={client.legal_entity_vat} onChange={(v) => setClient({ ...client, legal_entity_vat: v })} />
                    ) : (
                        <>
                            <Field label="Data di Nascita" value={client.birth_date} onChange={(v) => setClient({ ...client, birth_date: v })} type="date" />
                            <Field label="Luogo di Nascita" value={client.birth_place} onChange={(v) => setClient({ ...client, birth_place: v })} />
                        </>
                    )}
                    <Field label="Nazionalità" value={client.nationality} onChange={(v) => setClient({ ...client, nationality: v })} />
                    <Field label="Indirizzo di Residenza" value={client.residence_address} onChange={(v) => setClient({ ...client, residence_address: v })} />
                    <Field label="Città di Residenza" value={client.residence_city} onChange={(v) => setClient({ ...client, residence_city: v })} />
                    <SelectField label="Tipo Documento" value={client.document_type} onChange={(v) => setClient({ ...client, document_type: v })} options={DOC_KIND_OPTIONS} />
                    <Field label="Numero Documento" value={client.document_number} onChange={(v) => setClient({ ...client, document_number: v })} />
                    <Field label="Rilasciato da" value={client.document_issuer} onChange={(v) => setClient({ ...client, document_issuer: v })} />
                    <Field label="Data Rilascio" value={client.document_issue_date} onChange={(v) => setClient({ ...client, document_issue_date: v })} type="date" />
                    <Field label="Data Scadenza" value={client.document_expiry_date} onChange={(v) => setClient({ ...client, document_expiry_date: v })} type="date" />
                    <Field label="Telefono" value={client.phone} onChange={(v) => setClient({ ...client, phone: v })} />
                    <Field label="Email" value={client.email} onChange={(v) => setClient({ ...client, email: v })} />
                </Grid>
            </Section>

            <Section title="2. Dati dell'Operazione" icon={FileText}>
                <Grid>
                    <SelectField label="Tipo Prestazione" value={operation.tipo_prestazione} onChange={(v) => setOperation({ ...operation, tipo_prestazione: v })}
                        options={[{ value: "compravendita", label: "Compravendita" }, { value: "locazione", label: "Locazione" }, { value: "intermediazione", label: "Intermediazione" }, { value: "consulenza", label: "Consulenza" }]} />
                    <Field label="Immobile/Riferimento" value={operation.immobile_riferimento} onChange={(v) => setOperation({ ...operation, immobile_riferimento: v })} />
                    <Field label="Valore Operazione (€)" value={operation.valore_operazione} onChange={(v) => setOperation({ ...operation, valore_operazione: v })} type="number" />
                    <SelectField label="Modalità di Pagamento" value={operation.modalita_pagamento} onChange={(v) => setOperation({ ...operation, modalita_pagamento: v })}
                        options={[{ value: "bonifico", label: "Bonifico" }, { value: "contanti", label: "Contanti" }, { value: "assegno", label: "Assegno" }, { value: "mutuo", label: "Mutuo/Finanziamento" }, { value: "altro", label: "Altro" }]} />
                    <Field label="Data Operazione" value={operation.data_operazione} onChange={(v) => setOperation({ ...operation, data_operazione: v })} type="date" />
                </Grid>
            </Section>

            <Section title="3. Questionario di Adeguata Verifica" icon={CheckCircle2}>
                <Grid>
                    <Field label="Scopo e Natura della Prestazione" value={verification.scopo_natura_prestazione} onChange={(v) => setVerification({ ...verification, scopo_natura_prestazione: v })} />
                    <SelectField label="Origine dei Fondi" value={verification.origine_fondi} onChange={(v) => setVerification({ ...verification, origine_fondi: v })}
                        options={[{ value: "risparmi", label: "Risparmi personali" }, { value: "reddito_lavoro", label: "Reddito da lavoro" }, { value: "vendita_immobile", label: "Vendita di altro immobile" }, { value: "eredita", label: "Eredità/donazione" }, { value: "mutuo", label: "Mutuo/finanziamento" }, { value: "altro", label: "Altro" }]} />
                    <Field label="Note su origine fondi" value={verification.origine_fondi_note} onChange={(v) => setVerification({ ...verification, origine_fondi_note: v })} />
                </Grid>
                <label className="flex items-center gap-2 my-4 text-xs text-ink-400">
                    <input type="checkbox" checked={!!verification.agisce_per_conto_terzi} onChange={(e) => setVerification({ ...verification, agisce_per_conto_terzi: e.target.checked })} className="accent-gold" />
                    Il cliente agisce per conto di terzi
                </label>
                {verification.agisce_per_conto_terzi && (
                    <Field label="Nome del Terzo" value={verification.terzo_nome} onChange={(v) => setVerification({ ...verification, terzo_nome: v })} />
                )}
                {client.is_legal_entity && (
                    <Grid>
                        <Field label="Titolare Effettivo — Nome" value={verification.titolare_effettivo_nome} onChange={(v) => setVerification({ ...verification, titolare_effettivo_nome: v })} />
                        <Field label="Titolare Effettivo — Cod. Fiscale" value={verification.titolare_effettivo_cf} onChange={(v) => setVerification({ ...verification, titolare_effettivo_cf: v })} />
                        <Field label="Titolare Effettivo — Quota %" value={verification.titolare_effettivo_quota} onChange={(v) => setVerification({ ...verification, titolare_effettivo_quota: v })} />
                    </Grid>
                )}
                <label className="flex items-center gap-2 mt-4 mb-2 text-xs text-ink-400">
                    <input type="checkbox" checked={!!verification.documenti_raccolti_completi} onChange={(e) => setVerification({ ...verification, documenti_raccolti_completi: e.target.checked })} className="accent-gold" />
                    Documentazione raccolta completa
                </label>
                <Field label="Note dell'Agente" value={verification.note_agente} onChange={(v) => setVerification({ ...verification, note_agente: v })} textarea />
            </Section>

            <Section title="4. Valutazione del Profilo di Rischio" icon={AlertTriangle}>
                <div className="space-y-2 mb-5">
                    {RISK_QUESTIONS.map((q) => (
                        <label key={q.key} className="flex items-start gap-3 text-sm text-ink-200 cursor-pointer" data-testid={`aml-risk-q-${q.key}`}>
                            <input type="checkbox" checked={!!riskAnswers[q.key]} onChange={(e) => setRiskAnswers({ ...riskAnswers, [q.key]: e.target.checked })} className="mt-0.5 accent-gold" />
                            {q.label}
                        </label>
                    ))}
                </div>
                <div className={`border p-4 flex items-center justify-between ${RISK_LEVEL_STYLE[liveRisk.level]}`} data-testid="aml-risk-result">
                    <span className="text-sm font-semibold uppercase">Livello di Rischio: {liveRisk.level}</span>
                    <span className="text-sm">Punteggio: {liveRisk.score}/100</span>
                </div>
            </Section>

            <Section title="5. Documenti Raccolti" icon={Upload}>
                <div className="flex items-center gap-3 mb-4">
                    <select value={docKind} onChange={(e) => setDocKind(e.target.value)} className={`${inputCls} w-48`} data-testid="aml-doc-kind-select">
                        {DOC_KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <input ref={fileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} className="hidden" data-testid="aml-doc-file-input" id="aml-file-upload" />
                    <label htmlFor="aml-file-upload" className="cta-outline px-4 py-2 text-xs cursor-pointer flex items-center gap-1.5">
                        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Carica Documento
                    </label>
                </div>
                {(c.documents || []).length === 0 ? (
                    <div className="text-xs text-ink-500">Nessun documento caricato.</div>
                ) : (
                    <div className="space-y-2">
                        {c.documents.map((d) => (
                            <div key={d.id} className="border border-white/10 px-4 py-2.5 flex items-center justify-between" data-testid={`aml-doc-row-${d.id}`}>
                                <span className="text-sm text-ink-100 flex items-center gap-2"><FileText size={13} className="text-gold" /> {d.original_filename} <span className="text-[10px] text-ink-500 uppercase">({DOC_KIND_OPTIONS.find((o) => o.value === d.doc_kind)?.label})</span></span>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => downloadDoc(d.id, d.original_filename)} className="text-ink-400 hover:text-gold" data-testid={`aml-doc-download-${d.id}`}><Download size={14} /></button>
                                    <button onClick={() => deleteDoc(d.id)} className="text-ink-400 hover:text-red-400" data-testid={`aml-doc-delete-${d.id}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="border border-white/10 p-6">
            <h3 className="font-serif text-lg text-ink-50 mb-4 flex items-center gap-2"><Icon size={16} className="text-gold" /> {title}</h3>
            {children}
        </div>
    );
}

function Grid({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
    return (
        <div className={textarea ? "md:col-span-2" : ""}>
            <label className={labelCls}>{label}</label>
            {textarea ? (
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} h-20`} data-testid={testid} />
            ) : (
                <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
            )}
        </div>
    );
}

function SelectField({ label, value, onChange, options }) {
    return (
        <div>
            <label className={labelCls}>{label}</label>
            <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
                <option value="">— Seleziona —</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
